import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { useMaterialUIController } from "../context";
import theme from "../assets/theme";
import themeDark from "../assets/theme-dark";
import defaultBrandLight from "../assets/images/beaslogo_white.png";
import defaultBrandDark from "../assets/images/beaslogo_black.png";
/** High-res transparent mark (same artwork as plate PNG); SVG twin kept for marketing. */
import defaultBrandMark from "../assets/images/beas-report-icon.png";
import { clientBrandingService } from "../services/clientBrandingService";
import { buildBrandedTheme } from "../utils/brandedTheme";
import type { ClientBranding } from "../types/clientBranding";

const DEFAULT_BRANDING: ClientBranding = {
  appTitle: "Connect AI",
  logoUrl: "",
  logoUrlDark: "",
  primaryColor: "#3d7a76",
};

type ClientBrandingContextValue = {
  branding: ClientBranding;
  brandName: string;
  brandImageLight: string;
  brandImageDark: string;
  brandMark: string;
  loading: boolean;
};

const ClientBrandingContext = createContext<ClientBrandingContextValue>({
  branding: DEFAULT_BRANDING,
  brandName: DEFAULT_BRANDING.appTitle,
  brandImageLight: defaultBrandLight,
  brandImageDark: defaultBrandDark,
  brandMark: defaultBrandMark,
  loading: true,
});

export function useClientBranding() {
  return useContext(ClientBrandingContext);
}

export function ClientBrandingProvider({ children }: { children: ReactNode }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [branding, setBranding] = useState<ClientBranding>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientBrandingService
      .getBranding()
      .then((data) => setBranding({ ...DEFAULT_BRANDING, ...data }))
      .catch(() => setBranding(DEFAULT_BRANDING))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (branding.appTitle) {
      document.title = branding.appTitle;
    }
  }, [branding.appTitle]);

  const muiTheme = useMemo((): Theme => {
    const base = darkMode ? themeDark : theme;
    return buildBrandedTheme(base, branding.primaryColor);
  }, [darkMode, branding.primaryColor]);

  const brandImageLight = branding.logoUrl || defaultBrandLight;
  const brandImageDark = branding.logoUrlDark || branding.logoUrl || defaultBrandDark;
  const brandMark = defaultBrandMark;

  const value = useMemo(
    () => ({
      branding,
      brandName: branding.appTitle || DEFAULT_BRANDING.appTitle,
      brandImageLight,
      brandImageDark,
      brandMark,
      loading,
    }),
    [branding, brandImageLight, brandImageDark, brandMark, loading]
  );

  return (
    <ClientBrandingContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ClientBrandingContext.Provider>
  );
}
